<template>
  <div class="col-12 col-tl-7 pm__main">
    <div class="pm__thumbnails">
      <div class="pm__thumbnail-wrapper">
        <div
          v-for="(image, index) in carouselImages"
          :key="index"
          class="pm__thumbnail"
          tabindex="0"
          data-thumbnail
          :aria-roledescription="`This is a thumbnail for one of the variant of ${product.title}`"
        >
          <svg v-if="image.alt.includes('Fit Video')" class="pm__thumbnail-playIcon">
            <use xlink:href="#play-reverse-white"></use>
          </svg>
          <transition name="fade" mode="out-in">
            <ImageField
              :key="image.src"
              class="pm__thumbnail-image"
              :obj="{ url: image.src, alt: image.alt, width: 240, height: 240 }"
              width-param="width"
              sizes="480px"
            />
          </transition>
        </div>
      </div>
      <div class="pm__thumbnail-nav pm__thumbnail-prev" tabindex="0">
        <svg class="pm__thumbnail-prev-icon">
          <use xlink:href="#icon-prev"></use>
        </svg>
      </div>
      <div class="pm__thumbnail-nav pm__thumbnail-next" tabindex="0">
        <svg class="pm__thumbnail-next-icon">
          <use xlink:href="#icon-next"></use>
        </svg>
      </div>
    </div>
    <div class="pm__images">
      <div class="pm__slide-wrapper">
        <div
          v-for="(image, index) in carouselImages"
          :key="index"
          :class="['pm__slide', { 'pm__slide-test': isTestVar(index) }]"
        >
          <span
            :class="[
              'pm__images-count',
              { 'pm__images-count-test': reviewHeroTestVar === 'variation' && product.curatedReviews.review },
            ]"
            >{{ index + 1 }}/{{ carouselImages.length }}</span
          >
          <ZoomImage
            v-if="!image.link"
            class="pm__image"
            :scale="1.5"
            :img-normal="`${image.src}`"
            :img-alt="image.alt"
            :img-index="index"
          />
          <!-- If Type is video -->
          <div v-else class="pm__fit-video-wrapper">
            <iframe
              :src="`${image.link}?autoplay=1&loop=1&muted=1`"
              class="pm__fit-video"
              width="100%"
              height="100%"
              loading="lazy"
              tabindex="-1"
              aria-hidden="true"
            >
            </iframe>
            <div class="pm__fit-modal-info" v-html="product.fit_modal_content"></div>
          </div>
          <div v-if="isTestVar(index)" class="pm__curated-reviews">
            <div class="pm__curated-review" v-html="product.curatedReviews.review"></div>
            <div class="pm__curated-reviewer">
              <strong>{{ product.curatedReviews.reviewer }}</strong>
              <span class="pm__curated-reviewer-verified"> - Verified Buyer</span>
              <svg class="pm__curated-reviewer-icon">
                <use xlink:href="#verified-icon"></use>
              </svg>
            </div>
          </div>
        </div>
      </div>
      <div class="pm__nav pm__prev" tabindex="0">
        <svg class="pm__prev-icon">
          <use xlink:href="#icon-prev"></use>
        </svg>
      </div>
      <div class="pm__nav pm__next" tabindex="0">
        <svg class="pm__next-icon">
          <use xlink:href="#icon-next"></use>
        </svg>
      </div>
      <WishlistCTA :product="product" :selectedVariantDetails="productForm" customClass="pdp_wishlisticon" />
    </div>
    <!-- Fit Video Btn On mobile -->
    <div v-if="product.fit_video" class="pm__fit-video-mobile" @click="toggleVideoModal(true)">
      <svg class="pm__fit-video-icon">
        <use xlink:href="#play-reverse"></use>
      </svg>
      <span class="pm__fit-video-title">FIT VIDEO</span>
    </div>
    <!-- Fit Video Modal Mobile -->
    <div v-if="isModalopen" class="pm__fit-videoModal">
      <div class="pm__fit-videoModal-inner" :style="{ '--bgImage': 'url(' + product.fit_video_placeholder + ')' }">
        <div class="pm__fit-video-closeIcon-wrapper" @click="toggleVideoModal(false)">
          <svg class="pm__fit-video-closeIcon">
            <use xlink:href="#close"></use>
          </svg>
        </div>
        <iframe
          id="pm__fit-video-iframe"
          :data-src="`${product.fit_video_link}?autoplay=1&loop=1&muted=1`"
          width="304px"
          height="377px"
          loading="lazy"
        >
        </iframe>
        <div class="pm__fit-modal-info" v-html="product.fit_modal_content"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, onBeforeMount, onMounted, onUpdated, toRefs } from 'vue';
  import ImageField from '../../global/image.vue';
  import ZoomImage from '../../global/zoom-image.vue';
  import WishlistCTA from '../../global/wishlist-cta.vue';
  import Swiper from 'swiper';
  import SwiperCore, { Navigation, EffectFade, Thumbs } from 'swiper/core';

  SwiperCore.use([Navigation, EffectFade, Thumbs]);

  const props = defineProps({
    product: {
      type: Object,
      default: () => ({}),
    },

    productForm: {
      type: Object,
      default: () => ({}),
    },
  });

  const { product, productForm } = toRefs(props);
  const thumbnailSwiper = ref(null);
  const gallerySwiper = ref(null);
  const windowWidth = ref(window.innerWidth);
  const isModalopen = ref(false);
  const reviewHeroTestVar = ref(null);

  onBeforeMount(() => {

    // Set window width
    window.addEventListener('resize', () => {
      windowWidth.value = window.innerWidth;
    });
  });

  onMounted(() => {
    initializeSwipers();
  });

  onUpdated(() => {
    setTimeout(() => {
      destroySwipers();
      initializeSwipers();
    }, 100);
  });

  const carouselImages = computed(() => {
    const { title, images, fit_video, fit_video_placeholder, fit_video_link } = product.value;
    const { Color, Town } = productForm.value;
    var imageData = window.getProductImages(images, Color, title, Town);

    if (fit_video && windowWidth.value > 1023) {
      imageData = [
        ...imageData.slice(0, 1),
        {
          src: fit_video_placeholder,
          alt: 'Fit Video',
          link: fit_video_link,
        },
        ...imageData.slice(1),
      ];
    }

    return imageData;
  });

  const thumbnailCarousel = computed(() => {
    return {
      slidesPerView: 5.571,
      spaceBetween: 8,
      slidesOffsetAfter: 16,
      wrapperClass: 'pm__thumbnail-wrapper',
      slideClass: 'pm__thumbnail',
      navigation: {
        nextEl: '.pm__thumbnail-next',
        prevEl: '.pm__thumbnail-prev',
      },
      a11y: {
        enabled: true,
        itemRoleDescriptionMessage: `This is a thumbnail for one of the variant of ${product.value.title}`,
      },
      watchOverflow: true,
      breakpoints: {
        1024: {
          slidesPerView: 6,
          spaceBetween: 9,
          direction: 'vertical',
          threshold: 1000,
        },
      },
      on: {
        init: function () {
          const swiperInstance = this;
          const thumbnails = swiperInstance.slides; // Directly access Swiper slides
          const nextButton = document.querySelector('.pm__thumbnail-next');
          const prevButton = document.querySelector('.pm__thumbnail-prev');
          const isVisible = thumbnails.length > swiperInstance.params.slidesPerView;

          // Use classList to toggle visibility and reduce direct style manipulation
          nextButton.classList.toggle('hidden', !isVisible);
          prevButton.classList.toggle('hidden', !isVisible);

          // Add a custom keydown event listener for thumbnails to change using keyboard
          swiperInstance.slides.forEach((slide, index) => {
            slide.addEventListener('keydown', (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                if (swiperInstance && swiperInstance.slides?.length > index) {
                  swiperInstance.slideTo(index); // Navigate to the thumbnail
                }

                if (gallerySwiper.value && gallerySwiper.value?.slides?.length > index) {
                  gallerySwiper.value.slideTo(index); // Update the main gallery
                }
              }
            });
          });

        },
      },
    };
  });

  const galleryCarousel = computed(() => {
    return {
      slidesPerView: 1,
      spaceBetween: 2,
      wrapperClass: 'pm__slide-wrapper',
      slideClass: 'pm__slide',
      navigation: {
        nextEl: '.pm__next',
        prevEl: '.pm__prev',
      },
      thumbs: {
        swiper: thumbnailSwiper.value,
      },
      breakpoints: {
        1024: {
          spaceBetween: 4,
        },
      },
      a11y: {
        enabled: true,
        prevSlideMessage: `Go to previous carousel slide of the product ${product.value.title}`,
        nextSlideMessage: `Go to next carousel slide of product ${product.value.title}`,
        itemRoleDescriptionMessage: `This is a product carousel for one of the variant of ${product.value.title}`,
      },
    };
  });

  const toggleVideoModal = (value) => {
    isModalopen.value = value;

    setTimeout(() => {
      const videoIframe = document.querySelector('#pm__fit-video-iframe');
      if (value) {
        videoIframe.src = videoIframe.dataset.src;
      }
    }, 50);
  };

  const isTestVar = (index) => {
    return reviewHeroTestVar.value === 'variation' && index === 0 && product.value.curatedReviews.review;
  };

  const initializeSwipers = () => {
    thumbnailSwiper.value = new Swiper('.pm__thumbnails', thumbnailCarousel.value);
    gallerySwiper.value = new Swiper('.pm__images', galleryCarousel.value);
  };

  const destroySwipers = () => {
    thumbnailSwiper.value?.destroy();
    gallerySwiper.value?.destroy();
  };
</script>
